/* MolCellQuest — 永続化される学習状態
   ──────────────────────────────────────────────────────────
   Svelte 5 の $state で持つ。ネストしたプロパティの変更もそのまま
   リアクティブに伝わるので、変更したあと save() を呼ぶだけでよい。
   ────────────────────────────────────────────────────────── */

import { today } from "./format.js";

export const STATE_KEY = "molcellquest_state_v1";

/** 新規ユーザーの初期状態。migrate() の欠損補完にも使う。 */
function blank() {
  return {
    // 学習の記録
    solved: {}, //  problemId -> true（1度でも正解した）
    attempts: {}, //  problemId -> [{ t: epochMs, ok: bool, ms }]（最新50件）
    bestTimes: {}, //  problemId -> 正解時の最速ミリ秒
    solveTimeTotal: 0, //  累計の解答時間(ms)

    // 旅の進捗
    evo: 0, //  進化ポイント（単調増加）
    evoUnit: "v1", //  マイグレーション用マーカー
    eraId: "hadean", //  evo から導出できるが、変化検出のためキャッシュする
    milestonesReached: [], //  到達済みチェックポイント id（二重演出の防止）
    storiesSeen: [], //  ストーリーを見た時代 id

    // あとで解き直す（アカウント登録者限定の機能）
    //   problemId -> タイムスタンプ。正の値＝印あり、負の値＝外した記録（トムストーン）。
    //   端末をまたいで同期するとき、単純な和集合だと「外した」が復活してしまうので、
    //   絶対値の大きい（＝新しい）操作を勝たせるためにこの形にしている。
    review: {},

    // 間違えた問題の記録（アカウント登録者限定の機能）
    //   problemId -> { n: 誤答回数, w: 最後に間違えた時刻, c: 最後に克服した時刻 }
    //   「いま解き直すべきか」は w > c で判定する。フラグを持たずに時刻だけで
    //   表すのは、端末をまたぐマージを各フィールドの max だけで済ませるため。
    //   （別端末で克服 → こちらで誤答、の順序も時刻の大小で正しく決まる）
    missed: {},

    // プロフィール
    nickname: "",
    bio: "", //  自己紹介（ランキングでタップすると表示される）
    organism: null, //  { hue, pattern, eyes, accent }
    //  ニックネーム・見た目を最後に変更した時刻。
    //  起動時の自動セット（デフォルトの姿）では更新しない。0 のままなら
    //  「この端末では一度も自分で設定していない」＝クラウドの設定を優先してよい。
    profileUpdatedAt: 0,

    // 継続の仕掛け
    combo: 0,
    bestCombo: 0,
    streak: 0,
    bestStreak: 0,
    lastActive: "", //  "YYYY-MM-DD"
    activity: {}, //  "YYYY-MM-DD" -> その日の正解数
    missionClaimed: "", //  デイリーミッションを受け取った日

    // 設定・実績
    soundOn: true,
    badges: [],
  };
}

export const progress = $state(blank());

/** 1日のミッション目標（正解数） */
export const DAILY_GOAL = 3;

/* ── 永続化 ───────────────────────────────────────────── */

let saveTimer = null;

/** localStorage へ書き出す。連続呼び出しはまとめる。 */
export function save() {
  if (saveTimer) return;
  saveTimer = setTimeout(() => {
    saveTimer = null;
    saveNow();
  }, 0);
}

/** 同期的に書き出す（beforeunload や JSON 入出力から使う） */
export function saveNow() {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(snapshot()));
  } catch (e) {
    console.error("[MolCellQuest] 保存に失敗しました", e);
  }
  if (window.MCQSync && window.MCQSync.markDirty) window.MCQSync.markDirty();
}

/** $state プロキシを素のオブジェクトにする（構造化コピー） */
export function snapshot() {
  return JSON.parse(JSON.stringify(progress));
}

export function load() {
  let raw = null;
  try {
    raw = localStorage.getItem(STATE_KEY);
  } catch (e) {
    console.warn("[MolCellQuest] localStorage を読めません", e);
  }
  if (raw) {
    try {
      applyPlain(JSON.parse(raw));
    } catch (e) {
      console.error("[MolCellQuest] 保存データを解釈できませんでした", e);
    }
  }
  migrate();
  saveNow();
}

/** プレーンオブジェクトを状態に流し込む（未知のキーは無視する） */
function applyPlain(obj) {
  if (!obj || typeof obj !== "object") return;
  const allowed = blank();
  for (const k of Object.keys(allowed)) {
    if (obj[k] !== undefined) progress[k] = obj[k];
  }
}

/**
 * 欠損フィールドの補完と、スケール変更時の一発変換。
 * evoUnit のようなマーカーで「もう変換済みか」を判定するのが要点。
 */
export function migrate() {
  const defaults = blank();
  for (const [k, v] of Object.entries(defaults)) {
    if (progress[k] === undefined || progress[k] === null) {
      // organism だけは null を「未設定」として許容する
      if (k === "organism") continue;
      progress[k] = v;
    }
  }
  // 型が壊れているものを直す
  for (const k of ["solved", "attempts", "bestTimes", "activity", "review", "missed"]) {
    if (typeof progress[k] !== "object" || Array.isArray(progress[k])) progress[k] = {};
  }
  for (const k of ["milestonesReached", "storiesSeen", "badges"]) {
    if (!Array.isArray(progress[k])) progress[k] = [];
  }
  if (typeof progress.evo !== "number" || !isFinite(progress.evo) || progress.evo < 0) progress.evo = 0;

  // 将来 evo のスケールを変えたときは、ここに evoUnit で分岐した変換を書く。
  if (progress.evoUnit !== "v1") progress.evoUnit = "v1";
}

/** 進捗をまるごと消す（アカウント画面から呼ぶ） */
export function resetAll() {
  const fresh = blank();
  for (const k of Object.keys(fresh)) progress[k] = fresh[k];
  saveNow();
}

/* ── JSON エクスポート / インポート ───────────────────── */

export function exportJson() {
  const blob = new Blob([JSON.stringify(snapshot(), null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `molcellquest_backup_${today()}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * バックアップ JSON を読み込む。読み込み時と同じ migrate() を通すので
 * 古い形式のバックアップもそのまま復元できる。
 * @returns {{ ok: boolean, error?: string }}
 */
export function importJson(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { ok: false, error: "JSON として読み取れませんでした。" };
  }
  const looksValid =
    parsed && typeof parsed === "object" && parsed.solved && typeof parsed.evo === "number";
  if (!looksValid) {
    return { ok: false, error: "MolCellQuest のバックアップではないようです。" };
  }
  applyPlain(parsed);
  migrate();
  saveNow();
  return { ok: true };
}
